import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ExternalLink, Loader2, Network, Plus, Search, Sparkles, Trash2, UserRound, UserRoundPlus, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useTerminology } from "@/contexts/TerminologyContext";
import { normalizeRelationshipLabel, validateRelationshipSelection } from "@/lib/customerRelationships";

type RelationshipPerson = {
  person_id: string;
  customer_id: string | null;
  display_name: string;
  identity_state: "confirmed" | "inferred";
};

type RelationshipEdge = {
  relationship_id: string;
  source_person_id: string;
  target_person_id: string;
  relationship_type: string;
  inverse_relationship_type: string | null;
  is_directional: boolean;
  state: "confirmed" | "inferred" | "possible" | "dismissed";
  evidence_snippet: string | null;
};

type Connection = {
  edge: RelationshipEdge;
  person: RelationshipPerson;
  label: string;
  suggested: boolean;
  crmCustomerId: string | null;
  // Second-degree people, reached through this person.
  second: Connection[];
};

type RawConnection = {
  edge: RelationshipEdge;
  person: RelationshipPerson;
  crmCustomerId: string | null;
  fromId: string;
};

const RELATIONSHIP_TYPES = [
  { value: "family", label: "Family", inverse: "family", directional: false },
  { value: "spouse", label: "Spouse / partner", inverse: "spouse", directional: false },
  { value: "parent", label: "Parent of", inverse: "child", directional: true },
  { value: "sibling", label: "Sibling", inverse: "sibling", directional: false },
  { value: "friend", label: "Friend", inverse: "friend", directional: false },
  { value: "colleague", label: "Colleague", inverse: "colleague", directional: false },
  { value: "referred_by", label: "Referred by", inverse: "referred", directional: true },
  { value: "other", label: "Other", inverse: "other", directional: false },
] as const;

const ROW_HEIGHT = 60;
const ROW_GAP = 8;
const GROUP_HEADER_HEIGHT = 24;
const GROUP_GAP = 16;
const MIN_MAP_HEIGHT = 168;

const humanize = (value: string) => value.replace(/[_-]/g, " ").replace(/^\w/, (letter) => letter.toUpperCase());

const cleanPersonName = (value: string) => value
  .replace(/\s*\((?:inferred mention|inferred person|related person)\)\s*$/i, "")
  .trim();

const relationshipLabel = (edge: RelationshipEdge, fromId: string) => humanize(
  edge.source_person_id === fromId || !edge.is_directional
    ? edge.relationship_type
    : edge.inverse_relationship_type ?? edge.relationship_type,
);

function groupConnections(list: Connection[]) {
  const byLabel = new Map<string, Connection[]>();
  for (const connection of list) {
    byLabel.set(connection.label, [...(byLabel.get(connection.label) ?? []), connection]);
  }
  let offset = 0;
  const groups = [...byLabel.entries()]
    .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
    .map(([label, items]) => {
      const sorted = [...items].sort((a, b) => Number(a.suggested) - Number(b.suggested)
        || cleanPersonName(a.person.display_name).localeCompare(cleanPersonName(b.person.display_name)));
      const top = offset;
      const rows = sorted.map((connection, index) => ({
        connection,
        centerY: top + GROUP_HEADER_HEIGHT + index * (ROW_HEIGHT + ROW_GAP) + ROW_HEIGHT / 2,
      }));
      const height = GROUP_HEADER_HEIGHT + sorted.length * ROW_HEIGHT + (sorted.length - 1) * ROW_GAP;
      offset += height + GROUP_GAP;
      return { label, top, height, rows };
    });
  return { groups, height: groups.length ? offset - GROUP_GAP : 0 };
}

const initialsFor = (name: string) =>
  name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "?";

function Avatar({ name, inferred, className }: { name: string; inferred?: boolean; className?: string }) {
  return (
    <span
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border bg-muted/40 text-[11px] font-semibold text-foreground",
        inferred && "border-dashed border-primary/60 bg-primary/5 text-primary",
        className,
      )}
    >
      {initialsFor(name)}
    </span>
  );
}

// Pending AI suggestions touching this customer; drives the profile tab badge.
export function useRelationshipSuggestionCount(businessId: string | null | undefined, customerId: string | null | undefined) {
  const { data } = useQuery({
    queryKey: ["customer-relationship-suggestions", businessId, customerId],
    enabled: !!businessId && !!customerId,
    staleTime: 15_000,
    queryFn: async () => {
      const db = supabase as any;
      const { data: self } = await db
        .from("relationship_people")
        .select("person_id")
        .eq("business_id", businessId)
        .eq("customer_id", customerId)
        .maybeSingle();
      if (!self) return 0;
      const { count } = await db
        .from("customer_relationships")
        .select("relationship_id", { count: "exact", head: true })
        .eq("business_id", businessId)
        .in("state", ["inferred", "possible"])
        .or(`source_person_id.eq.${self.person_id},target_person_id.eq.${self.person_id}`);
      return (count ?? 0) as number;
    },
  });
  return data ?? 0;
}

interface CustomerRelationshipsProps {
  businessId: string;
  customerId: string;
  customerName: string;
  onOpenCustomer: (customerId: string) => void;
}

export default function CustomerRelationships({
  businessId,
  customerId,
  customerName,
  onOpenCustomer,
}: CustomerRelationshipsProps) {
  const { customer: customerTerm } = useTerminology();
  const queryClient = useQueryClient();
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const queryKey = ["customer-relationships-profile", "v2", businessId, customerId];
  const { data, isLoading, error } = useQuery({
    queryKey,
    enabled: !!businessId && !!customerId,
    staleTime: 15_000,
    queryFn: async () => {
      const db = supabase as any;
      const { data: self, error: selfError } = await db
        .from("relationship_people")
        .select("person_id, customer_id, display_name, identity_state")
        .eq("business_id", businessId)
        .eq("customer_id", customerId)
        .maybeSingle();
      if (selfError) throw selfError;
      if (!self) return { self: null as RelationshipPerson | null, connections: [] as (RawConnection & { second: RawConnection[] })[] };

      const edgeColumns = "relationship_id, source_person_id, target_person_id, relationship_type, inverse_relationship_type, is_directional, state, evidence_snippet";
      const { data: edges, error: edgesError } = await db
        .from("customer_relationships")
        .select(edgeColumns)
        .eq("business_id", businessId)
        .neq("state", "dismissed")
        .or(`source_person_id.eq.${self.person_id},target_person_id.eq.${self.person_id}`)
        .order("created_at", { ascending: true });
      if (edgesError) throw edgesError;

      const directEdges = (edges ?? []) as RelationshipEdge[];
      const otherIds = [...new Set(directEdges.map((edge) => (
        edge.source_person_id === self.person_id ? edge.target_person_id : edge.source_person_id
      )))];
      if (otherIds.length === 0) return { self: self as RelationshipPerson, connections: [] };

      // Second degree: everything the direct connections are linked to.
      const idList = otherIds.join(",");
      const { data: hopRows, error: hopError } = await db
        .from("customer_relationships")
        .select(edgeColumns)
        .eq("business_id", businessId)
        .neq("state", "dismissed")
        .or(`source_person_id.in.(${idList}),target_person_id.in.(${idList})`)
        .order("created_at", { ascending: true });
      if (hopError) throw hopError;

      const directSet = new Set(otherIds);
      const hops: { edge: RelationshipEdge; fromId: string; toId: string }[] = [];
      for (const edge of (hopRows ?? []) as RelationshipEdge[]) {
        for (const [fromId, toId] of [[edge.source_person_id, edge.target_person_id], [edge.target_person_id, edge.source_person_id]]) {
          if (directSet.has(fromId) && toId !== self.person_id) hops.push({ edge, fromId, toId });
        }
      }

      const everyId = [...new Set([...otherIds, ...hops.map((hop) => hop.toId)])];
      const { data: peopleRows, error: peopleError } = await db
        .from("relationship_people")
        .select("person_id, customer_id, display_name, identity_state")
        .eq("business_id", businessId)
        .in("person_id", everyId);
      if (peopleError) throw peopleError;
      const peopleById = new Map<string, RelationshipPerson>(
        ((peopleRows ?? []) as RelationshipPerson[]).map((person) => [person.person_id, person]),
      );

      // Related people that were never linked to a record may still exist in
      // the CRM under the same name; match those so they open their profile.
      const unlinkedNames = [...new Set(
        [...peopleById.values()].filter((person) => !person.customer_id).map((person) => person.display_name),
      )];
      const customerIdByName = new Map<string, string>();
      if (unlinkedNames.length > 0) {
        const { data: matches } = await db
          .from("customers")
          .select("customer_id, name")
          .eq("business_id", businessId)
          .neq("customer_id", customerId)
          .in("name", unlinkedNames);
        const seen = new Set<string>();
        for (const row of (matches ?? []) as { customer_id: string; name: string }[]) {
          const key = row.name.trim().toLowerCase();
          // Ambiguous names (two customers) stay unlinked.
          if (customerIdByName.has(key) || seen.has(key)) {
            customerIdByName.delete(key);
            seen.add(key);
          } else {
            customerIdByName.set(key, row.customer_id);
          }
        }
      }
      const crmIdFor = (person: RelationshipPerson) =>
        person.customer_id ?? customerIdByName.get(person.display_name.trim().toLowerCase()) ?? null;

      const connections = directEdges.flatMap((edge) => {
        const otherId = edge.source_person_id === self.person_id ? edge.target_person_id : edge.source_person_id;
        const person = peopleById.get(otherId);
        if (!person) return [];
        const second = hops
          .filter((hop) => hop.fromId === otherId && peopleById.has(hop.toId))
          .map((hop) => ({
            edge: hop.edge,
            person: peopleById.get(hop.toId)!,
            crmCustomerId: crmIdFor(peopleById.get(hop.toId)!),
            fromId: otherId,
          }));
        return [{ edge, person, crmCustomerId: crmIdFor(person), fromId: self.person_id as string, second }];
      });
      return { self: self as RelationshipPerson, connections };
    },
  });

  const connections = useMemo<Connection[]>(() => {
    if (!data?.self) return [];
    const build = (raw: RawConnection): Omit<Connection, "second"> => ({
      edge: raw.edge,
      person: raw.person,
      crmCustomerId: raw.crmCustomerId,
      suggested: raw.edge.state !== "confirmed",
      label: relationshipLabel(raw.edge, raw.fromId),
    });
    return data.connections
      .map((raw) => ({
        ...build(raw),
        second: (raw.second ?? [])
          .map((child) => ({ ...build(child), second: [] as Connection[] }))
          .filter((child) => showSuggestions || !child.suggested),
      }))
      .filter((connection) => showSuggestions || !connection.suggested);
  }, [data, showSuggestions]);

  const suggestionCount = useMemo(() => {
    const ids = new Set<string>();
    for (const raw of data?.connections ?? []) {
      if (raw.edge.state !== "confirmed") ids.add(raw.edge.relationship_id);
      for (const child of raw.second ?? []) if (child.edge.state !== "confirmed") ids.add(child.edge.relationship_id);
    }
    return ids.size;
  }, [data]);

  const { groups, height: firstGroupsHeight } = useMemo(() => groupConnections(connections), [connections]);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const expanded = useMemo(() => {
    for (const group of groups) {
      const row = group.rows.find(({ connection }) => connection.person.person_id === expandedId);
      if (row && row.connection.second.length > 0) return row;
    }
    return null;
  }, [expandedId, groups]);

  const firstColumnHeight = Math.max(MIN_MAP_HEIGHT, firstGroupsHeight);
  const originY = firstColumnHeight / 2;

  // Second-degree people fan out beside the person that was clicked, grouped
  // by relationship exactly like the first column.
  const second = useMemo(
    () => groupConnections(expanded?.connection.second ?? []),
    [expanded],
  );
  const secondTop = expanded ? Math.max(0, expanded.centerY - second.height / 2) : 0;
  const mapHeight = Math.max(firstColumnHeight, secondTop + second.height);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey });
    await queryClient.invalidateQueries({ queryKey: ["customer-relationship-suggestions", businessId] });
  };

  const review = async (relationshipId: string, action: "confirm" | "dismiss") => {
    if (busyId) return;
    setBusyId(relationshipId);
    try {
      const { data: reviewed, error: reviewError } = await (supabase as any)
        .from("customer_relationships")
        .update(action === "confirm"
          ? { state: "confirmed", confidence: 1, confirmed_at: new Date().toISOString() }
          : { state: "dismissed" })
        .eq("business_id", businessId)
        .eq("relationship_id", relationshipId)
        .select("relationship_id")
        .maybeSingle();
      if (reviewError) throw reviewError;
      if (!reviewed) throw new Error("Relationship no longer exists");
      toast.success(action === "confirm" ? "Relationship confirmed" : "Suggestion dismissed");
      await refresh();
    } catch {
      toast.error(`Couldn't ${action} relationship`);
    } finally {
      setBusyId(null);
    }
  };

  const unlink = async (relationshipId: string) => {
    if (busyId) return;
    if (!window.confirm(`Remove this relationship? The people and their ${customerTerm.toLowerCase()} records will remain.`)) return;
    setBusyId(relationshipId);
    try {
      const { data: removed, error: deleteError } = await (supabase as any)
        .from("customer_relationships")
        .delete()
        .eq("business_id", businessId)
        .eq("relationship_id", relationshipId)
        .select("relationship_id")
        .maybeSingle();
      if (deleteError) throw deleteError;
      if (!removed) throw new Error("Relationship no longer exists");
      toast.success("Relationship removed");
      await refresh();
    } catch {
      toast.error("Couldn't remove relationship");
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) {
    return <div className="flex min-h-[240px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  if (error) {
    return (
      <div className="flex min-h-[240px] flex-col items-center justify-center rounded-lg border border-dashed text-center">
        <Network className="mb-3 h-8 w-8 text-muted-foreground" />
        <p className="font-medium">Relationships aren't available yet</p>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">Apply the {customerTerm.toLowerCase()} relationship graph migration, then refresh this page.</p>
      </div>
    );
  }

  const displayName = cleanPersonName(customerName);

  return (
    <section aria-label="Relationships map">
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <div className="flex flex-wrap items-start gap-3 p-5 pb-0">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-base font-semibold">Relationships map</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {connections.length === 0
                ? `People connected to ${displayName}, like family, friends and colleagues. Relationships the AI spots in conversations show up here for review.`
                : `${connections.length} ${connections.length === 1 ? "person" : "people"} connected to ${displayName}. Select someone to see who they're connected to.`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {suggestionCount > 0 && (
              <div className="flex items-center gap-2">
                <Switch id="profile-relationship-suggestions" checked={showSuggestions} onCheckedChange={setShowSuggestions} />
                <Label htmlFor="profile-relationship-suggestions" className="flex cursor-pointer items-center gap-1.5 text-xs font-normal">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  AI suggestions ({suggestionCount})
                </Label>
              </div>
            )}
            <Button variant="outline" size="sm" className="admin-toolbar-button font-normal" onClick={() => setDialogOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          </div>
        </div>
        {connections.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <Avatar name={displayName} className="h-12 w-12 border-transparent bg-primary text-sm text-primary-foreground" />
            <p className="mt-3 text-sm font-medium">{displayName} isn't connected to anyone yet</p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Add a parent, sibling, friend or colleague. Relationships the AI picks up from conversations will appear here for you to review.
            </p>
            <Button size="sm" className="standard-action-button mt-4" onClick={() => setDialogOpen(true)}>
              <UserRoundPlus className="mr-1.5 h-4 w-4" />
              Add relationship
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto p-5">
            <div
              className={cn(
                "grid",
                expanded
                  ? "min-w-[980px] grid-cols-[200px_minmax(56px,1fr)_minmax(260px,320px)_minmax(56px,1fr)_minmax(260px,320px)]"
                  : "min-w-[560px] grid-cols-[200px_minmax(72px,1fr)_minmax(260px,340px)]",
              )}
              style={{ height: mapHeight }}
            >
              <div className="relative">
                <div
                  className="absolute inset-x-0 flex -translate-y-1/2 items-center gap-3 rounded-lg border border-border bg-card p-3 shadow-sm"
                  style={{ top: originY }}
                >
                  <Avatar name={displayName} className="bg-primary text-primary-foreground border-transparent" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{displayName}</p>
                    <p className="text-xs text-muted-foreground">This {customerTerm.toLowerCase()}</p>
                  </div>
                </div>
              </div>

              <Curves
                height={mapHeight}
                fromY={originY}
                lines={groups.flatMap((group) => group.rows.map(({ connection, centerY }) => ({
                  key: connection.edge.relationship_id,
                  toY: centerY,
                  suggested: connection.suggested,
                })))}
              />

              <div className="relative">
                {groups.map((group) => (
                  <div key={group.label} className="absolute inset-x-0" style={{ top: group.top, height: group.height }}>
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground" style={{ height: GROUP_HEADER_HEIGHT }}>
                      {group.label}
                      <span className="font-medium normal-case tracking-normal">{group.rows.length}</span>
                    </p>
                    <div className="flex flex-col" style={{ gap: ROW_GAP }}>
                      {group.rows.map(({ connection }) => (
                        <ConnectionCard
                          key={connection.edge.relationship_id}
                          connection={connection}
                          expandable={connection.second.length > 0}
                          expanded={expanded?.connection.person.person_id === connection.person.person_id}
                          busy={busyId === connection.edge.relationship_id}
                          disabled={busyId !== null}
                          onSelect={() => setExpandedId((current) => current === connection.person.person_id ? null : connection.person.person_id)}
                          onOpen={onOpenCustomer}
                          onConfirm={() => review(connection.edge.relationship_id, "confirm")}
                          onDismiss={() => review(connection.edge.relationship_id, "dismiss")}
                          onUnlink={() => unlink(connection.edge.relationship_id)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {expanded && (
                <>
                  <Curves
                    height={mapHeight}
                    fromY={expanded.centerY}
                    lines={second.groups.flatMap((group) => group.rows.map(({ connection, centerY }) => ({
                      key: connection.edge.relationship_id,
                      toY: secondTop + centerY,
                      suggested: connection.suggested,
                    })))}
                  />
                  <div className="relative">
                    {second.groups.map((group) => (
                      <div key={group.label} className="absolute inset-x-0" style={{ top: secondTop + group.top, height: group.height }}>
                        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground" style={{ height: GROUP_HEADER_HEIGHT }}>
                          {group.label}
                          <span className="font-medium normal-case tracking-normal">{group.rows.length}</span>
                        </p>
                        <div className="flex flex-col" style={{ gap: ROW_GAP }}>
                          {group.rows.map(({ connection }) => (
                            <ConnectionCard
                              key={connection.edge.relationship_id}
                              connection={connection}
                              busy={busyId === connection.edge.relationship_id}
                              disabled={busyId !== null}
                              onOpen={onOpenCustomer}
                              onConfirm={() => review(connection.edge.relationship_id, "confirm")}
                              onDismiss={() => review(connection.edge.relationship_id, "dismiss")}
                              onUnlink={() => unlink(connection.edge.relationship_id)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <AddRelationshipDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        businessId={businessId}
        customerId={customerId}
        customerName={displayName}
        onSaved={refresh}
      />
    </section>
  );
}

function Curves({
  height,
  fromY,
  lines,
}: {
  height: number;
  fromY: number;
  lines: { key: string; toY: number; suggested: boolean }[];
}) {
  return (
    <svg className="h-full w-full" viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" aria-hidden="true">
      {lines.map((line) => (
        <path
          key={line.key}
          d={`M 0 ${fromY} C 55 ${fromY}, 45 ${line.toY}, 100 ${line.toY}`}
          fill="none"
          stroke={line.suggested ? "hsl(var(--primary))" : "hsl(var(--border))"}
          strokeWidth={line.suggested ? 1.25 : 1.5}
          strokeDasharray={line.suggested ? "4 4" : undefined}
          vectorEffect="non-scaling-stroke"
          opacity={line.suggested ? 0.7 : 1}
        />
      ))}
    </svg>
  );
}

function ConnectionCard({
  connection,
  expandable = false,
  expanded = false,
  busy,
  disabled,
  onSelect,
  onOpen,
  onConfirm,
  onDismiss,
  onUnlink,
}: {
  connection: Connection;
  expandable?: boolean;
  expanded?: boolean;
  busy: boolean;
  disabled: boolean;
  onSelect?: () => void;
  onOpen: (customerId: string) => void;
  onConfirm: () => void;
  onDismiss: () => void;
  onUnlink: () => void;
}) {
  const { person, suggested, second, edge, crmCustomerId } = connection;
  const name = cleanPersonName(person.display_name);
  const inferred = person.identity_state === "inferred";
  const subtitle = [
    crmCustomerId ? "Customer" : inferred ? "AI-inferred person" : "Related person",
    second.length > 0 ? `${second.length} other ${second.length === 1 ? "connection" : "connections"}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div
      className={cn(
        "group flex items-center gap-3 rounded-lg border bg-card px-3 shadow-sm transition-colors",
        suggested ? "border-dashed border-primary/50 bg-primary/5" : "border-border hover:bg-muted/40",
        expanded && "border-foreground/30 bg-muted/40",
        expandable && "cursor-pointer",
      )}
      onClick={expandable ? onSelect : undefined}
      style={{ height: ROW_HEIGHT }}
      title={suggested && edge.evidence_snippet ? `“${edge.evidence_snippet}”` : undefined}
    >
      <Avatar name={name} inferred={inferred} />
      <div className="min-w-0 flex-1">
        <span className="flex items-center gap-1">
          <span className="truncate text-sm font-medium">{name}</span>
          {crmCustomerId && (
            <button
              type="button"
              className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
              onClick={(event) => { event.stopPropagation(); onOpen(crmCustomerId); }}
              aria-label={`Open ${name}'s profile in a new tab`}
              title="Open profile in a new tab"
            >
              <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </span>
        <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
      </div>
      {suggested ? (
        <div className="flex shrink-0 items-center gap-1">
          <Button size="icon" variant="outline" className="h-7 w-7 neutral-action-button" disabled={disabled} onClick={(event) => { event.stopPropagation(); onConfirm(); }} aria-label={`Confirm relationship with ${name}`} title="Confirm">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </Button>
          <Button size="icon" variant="outline" className="h-7 w-7 neutral-action-button" disabled={disabled} onClick={(event) => { event.stopPropagation(); onDismiss(); }} aria-label={`Dismiss suggestion for ${name}`} title="Dismiss">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
          disabled={disabled}
          onClick={(event) => { event.stopPropagation(); onUnlink(); }}
          aria-label={`Remove relationship with ${name}`}
          title="Remove relationship"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        </Button>
      )}
    </div>
  );
}

function AddRelationshipDialog({
  open,
  onOpenChange,
  businessId,
  customerId,
  customerName,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  customerId: string;
  customerName: string;
  onSaved: () => Promise<void>;
}) {
  const { customer: customerTerm } = useTerminology();
  const [targetKind, setTargetKind] = useState<"customer" | "person">("customer");
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState<{ customer_id: string; name: string } | null>(null);
  const [relatedName, setRelatedName] = useState("");
  const [relationshipType, setRelationshipType] = useState("family");
  const [customRelationship, setCustomRelationship] = useState("");
  const [saving, setSaving] = useState(false);

  const term = search.trim();
  const { data: matches = [], isFetching } = useQuery({
    queryKey: ["relationship-customer-search", businessId, term],
    enabled: open && targetKind === "customer" && !target && term.length >= 2,
    queryFn: async () => {
      const { data: rows, error } = await (supabase as any)
        .from("customers")
        .select("customer_id, name")
        .eq("business_id", businessId)
        .neq("customer_id", customerId)
        .ilike("name", `%${term.replace(/[%_,()]/g, " ")}%`)
        .order("name")
        .limit(8);
      if (error) throw error;
      return (rows ?? []) as { customer_id: string; name: string }[];
    },
  });

  const reset = () => {
    setTargetKind("customer");
    setSearch("");
    setTarget(null);
    setRelatedName("");
    setRelationshipType("family");
    setCustomRelationship("");
  };

  const typeMeta = RELATIONSHIP_TYPES.find((item) => item.value === relationshipType);

  const save = async () => {
    if (saving || !typeMeta) return;
    const finalType = normalizeRelationshipLabel(relationshipType === "other" ? customRelationship : typeMeta.value);
    const targetCustomerId = target?.customer_id ?? "";
    const validationError = validateRelationshipSelection({
      sourceCustomerId: customerId,
      targetCustomerId,
      targetKind,
      relatedName,
      relationshipLabel: finalType,
      knownCustomerIds: new Set([customerId, targetCustomerId].filter(Boolean)),
    });
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSaving(true);
    try {
      const { data: result, error } = await (supabase as any).rpc("create_customer_relationship", {
        p_business_id: businessId,
        p_source_customer_id: customerId,
        p_relationship_type: finalType,
        p_target_customer_id: targetKind === "customer" ? targetCustomerId : null,
        p_related_name: targetKind === "person" ? normalizeRelationshipLabel(relatedName) : null,
        p_inverse_relationship_type: relationshipType === "other" ? finalType : typeMeta.inverse,
        p_is_directional: relationshipType === "other" ? false : typeMeta.directional,
      });
      if (error) throw error;
      toast.success(result?.created === false ? "Relationship already exists" : "Relationship added");
      onOpenChange(false);
      reset();
      await onSaved();
    } catch (saveError: any) {
      toast.error(saveError.message || "Couldn't add relationship");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { onOpenChange(next); if (!next) reset(); }}>
      <DialogContent className="admin-modal-content sm:max-w-[480px]">
        <DialogHeader className="admin-modal-header">
          <DialogTitle className="font-heading">Add relationship</DialogTitle>
          <DialogDescription>Connect {customerName} to another {customerTerm.toLowerCase()}, or to someone who isn't one yet.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Connected to</Label>
            <div className="flex rounded-md border bg-card p-1">
              <Button type="button" variant={targetKind === "customer" ? "secondary" : "ghost"} size="sm" className="h-7 flex-1" onClick={() => setTargetKind("customer")}>
                <UserRound className="mr-1.5 h-3.5 w-3.5" />Existing {customerTerm.toLowerCase()}
              </Button>
              <Button type="button" variant={targetKind === "person" ? "secondary" : "ghost"} size="sm" className="h-7 flex-1" onClick={() => setTargetKind("person")}>
                <UserRoundPlus className="mr-1.5 h-3.5 w-3.5" />Related person
              </Button>
            </div>
            {targetKind === "customer" ? (
              target ? (
                <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm">
                  <Avatar name={target.name} className="h-7 w-7 text-[10px]" />
                  <span className="min-w-0 flex-1 truncate font-medium">{target.name}</span>
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => setTarget(null)} aria-label="Choose someone else"><X className="h-3.5 w-3.5" /></Button>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`Search ${customerTerm.toLowerCase()} by name`} className="pl-8" autoFocus />
                  </div>
                  {term.length >= 2 && (
                    <div className="max-h-48 overflow-y-auto rounded-md border bg-card">
                      {isFetching && matches.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-muted-foreground">Searching…</p>
                      ) : matches.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-muted-foreground">No matching {customerTerm.toLowerCase()}.</p>
                      ) : matches.map((match) => (
                        <button key={match.customer_id} type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => setTarget(match)}>
                          <Avatar name={match.name} className="h-6 w-6 text-[9px]" />
                          <span className="truncate">{match.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            ) : (
              <Input value={relatedName} maxLength={200} onChange={(event) => setRelatedName(event.target.value)} placeholder="Person's name" />
            )}
          </div>
          <div className="space-y-2">
            <Label>Relationship</Label>
            <Select value={relationshipType} onValueChange={setRelationshipType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RELATIONSHIP_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {relationshipType === "other" && (
              <Input value={customRelationship} maxLength={80} onChange={(event) => setCustomRelationship(event.target.value)} placeholder="e.g. neighbour, coach, caregiver" />
            )}
            {typeMeta?.directional && (
              <p className="text-xs text-muted-foreground">This will read: {customerName} → {typeMeta.label} → person.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" className="standard-action-button" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" className="standard-action-button" onClick={save} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add relationship
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
